import { createFileRoute } from "@tanstack/react-router";
import { IoWarningOutline } from "react-icons/io5";
import { Trans, useTranslation } from "react-i18next";
import { css } from "../../../../../styled-system/css";
import { Center, VStack } from "../../../../../styled-system/jsx";
import { Button } from "../../../../components/core/basic/Button";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import Loading from "../../../../components/core/blocks/Loading";
import { useAppSelector } from "../../../../redux/hooks";
import { useAppsStatus } from "../../../../modules/admin/api/useAppsStatus";
import { useConnectShop } from "../../../../modules/shopify/api/useConnectShop";
import Card from "../../../../components/core/basic/Card";
import { successToast, errorToast } from "../../../../utils/toasts";

function Connect() {
  const isStaff = useAppSelector((state) => state.user.is_staff);
  const { t } = useTranslation(["shopify"]);
  const shop_url = new URLSearchParams(window.location.search).get("shop");
  const connectShopMutation = useConnectShop();
  const appsStatusQuery = useAppsStatus();
  if (appsStatusQuery.isLoading) {
    return <Loading fullPage />;
  }

  if (!appsStatusQuery.isSuccess) {
    throw new Error("Failed to load apps status");
  }

  const shops = appsStatusQuery.data.shopify.shops;

  return (
    <PageContainer>
      {shop_url && !shops.includes(shop_url) && (
        <Card>
          <h4 className={css({ margin: 0 })}>
            <Trans
              t={t}
              i18nKey={($) => $.shopify["Connect shop <strong><i>{shopUrl}</i></strong>?"]}
              values={{ shopUrl: shop_url }}
              components={{ strong: <strong />, i: <i /> }}
            />
          </h4>
          {isStaff && (
            <VStack
              alignItems="start"
              border={"1px solid {black}"}
              paddingY="medium"
              paddingX={"large"}
              fontSize="medium"
              backgroundColor={"yellow/50"}
            >
              <h5 className={css({ margin: 0 })}>
                <IoWarningOutline className={css({ fontSize: "xxlarge" })} />{" "}
                {t(($) => $.shopify["Check which account you're connecting to"])}
              </h5>
              {t(
                ($) =>
                  $.shopify[
                    "As a staff user you can be looking at any account in the system. Make sure the right one is selected before connecting the shopify store"
                  ],
              )}
            </VStack>
          )}
          <Center marginTop="medium">
            <Button
              isPending={connectShopMutation.isPending}
              onPress={() =>
                connectShopMutation.mutate(
                  { shop: shop_url },
                  {
                    onSuccess: () => {
                      successToast({ message: t(($) => $.shopify["Shop connected successfully"]) });
                      void appsStatusQuery.refetch();
                    },
                    onError: (error) => {
                      errorToast({ message: t(($) => $.shopify["Error connecting shop"]), error });
                    },
                  },
                )
              }
            >
              {t(($) => $.shopify["Connect"])}
            </Button>
          </Center>
        </Card>
      )}
      {shop_url && shops.includes(shop_url) && (
        <Card>
          <h4 className={css({ margin: 0 })}>{t(($) => $.shopify["Shop connected successfully"])}</h4>
          <p>
            <Trans
              t={t}
              i18nKey={($) => $.shopify["<strong><i>{shopUrl}</i></strong> is connected"]}
              values={{ shopUrl: shop_url }}
              components={{ strong: <strong />, i: <i /> }}
            />
          </p>
        </Card>
      )}
      <Card>
        <h4 className={css({ margin: 0 })}>{t(($) => $.shopify["Shopify connections"])}</h4>
        {shops.length ?
          <figure className={css({ fontSize: "medium" })}>
            <figcaption>{t(($) => $.shopify["Connected Shops:"])}</figcaption>
            <ul>
              {shops.map((shop) => (
                <li key={shop}>{shop}</li>
              ))}
            </ul>
          </figure>
        : <p>{t(($) => $.shopify["No shops currently connected"])}</p>}
        {shop_url && !shops.includes(shop_url) && <VStack alignItems={"start"}></VStack>}
      </Card>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_authenticated/user/apps/shopify")({
  component: Connect,
  staticData: { application: "Admin" },
});
